<template>
  <div>
    <b-card no-body class="users-card">
      <!-- Filter bar -->
      <div class="filter-bar p-3 border-bottom">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <b-input-group size="sm" class="search-group">
            <b-input-group-prepend is-text>
              <i class="fas fa-search"></i>
            </b-input-group-prepend>
            <b-form-input v-model="searchText" placeholder="ค้นหาชื่อ / รหัสแพทย์..." />
          </b-input-group>

          <div class="ml-auto d-flex align-items-center" style="gap: 0.5rem">
            <small v-if="!isLoading" class="text-muted">
              {{ filteredDoctors.length }} / {{ doctors.length }} คน
            </small>
            <b-button size="sm" variant="outline-secondary" @click="refresh">
              <b-spinner v-if="isLoading" small class="mr-1" />
              <i v-else class="fas fa-sync-alt mr-1"></i>
              รีเฟรช
            </b-button>
          </div>
        </div>
      </div>

      <b-table
        :items="filteredDoctors"
        :fields="fields"
        :busy="isLoading"
        hover
        responsive
        empty-text="ไม่มีแพทย์"
        show-empty
        class="user-table mb-0"
        thead-class="user-table__head"
        tbody-tr-class="user-table__row"
      >
        <template #table-busy>
          <div class="text-center py-4">
            <b-spinner class="align-middle" />
            <span class="ml-2">กำลังโหลด...</span>
          </div>
        </template>

        <template #cell(index)="row">
          <span class="row-index">{{ row.index + 1 }}</span>
        </template>

        <template #cell(name)="row">
          <strong class="user-name">
            {{ row.item.firstNameTh }} {{ row.item.lastNameTh }}
          </strong>
        </template>

        <template #cell(doctorCode)="row">
          <span class="doctor-code">{{ row.item.doctorCode || "—" }}</span>
          <i
            v-if="codeWarning(row.item).warn"
            class="fas fa-exclamation-triangle text-warning ml-1"
            :title="codeWarning(row.item).message"
          ></i>
        </template>

        <template #cell(branches)="row">
          <div
            v-if="doctorBranches(row.item).length"
            class="d-flex flex-wrap"
            style="gap: 0.35rem"
          >
            <span
              v-for="b in doctorBranches(row.item)"
              :key="b.id"
              class="branch-pill"
              :class="{ 'branch-pill--missing': !b.doctorCode }"
              :title="!b.doctorCode ? 'ไม่มีรหัสแพทย์ในสาขานี้' : ''"
            >
              <i class="fas fa-store-alt mr-1"></i>{{ b.name || "—" }}
            </span>
          </div>
          <span v-else class="text-muted small">—</span>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import axios from "axios";

const DOCTOR_ROLE_KEY = "DOCTOR";

export default {
  name: "ClinicDoctors",
  data() {
    return {
      doctors: [],
      isLoading: false,
      searchText: "",
      fields: [
        {
          key: "index",
          label: "#",
          thStyle: { width: "48px" },
          tdClass: "align-middle",
        },
        { key: "name", label: "ชื่อ", sortable: true, tdClass: "align-middle" },
        {
          key: "doctorCode",
          label: "รหัสแพทย์",
          sortable: true,
          tdClass: "align-middle",
        },
        {
          key: "branches",
          label: "สาขาที่เป็นแพทย์",
          tdClass: "align-middle",
        },
      ],
    };
  },
  computed: {
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    // Only people who hold the DOCTOR branch role in at least one branch.
    filteredDoctors() {
      const q = this.searchText.trim().toLowerCase();
      return this.doctors.filter(u => {
        if (!q) return true;
        const name = `${u.firstNameTh || ""} ${u.lastNameTh || ""}`.toLowerCase();
        const code = String(u.doctorCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    },
  },
  async created() {
    await this.ensurePermissionContext();
    await this.refresh();
  },
  methods: {
    // Branches where this user holds the DOCTOR role.
    doctorBranches(u) {
      return (u.branches || []).filter(b =>
        (b.roleKeys || []).includes(DOCTOR_ROLE_KEY)
      );
    },
    // Warn when a doctor-role branch is missing a code, or codes differ.
    codeWarning(u) {
      const db = this.doctorBranches(u);
      const codes = [...new Set(db.map(b => b.doctorCode).filter(Boolean))];
      const missing = db.some(b => !b.doctorCode);
      if (!missing && codes.length <= 1) return { warn: false, message: "" };
      const parts = [];
      if (missing) parts.push("ไม่มีรหัสแพทย์ในบางสาขา");
      if (codes.length > 1) parts.push(`รหัสไม่ตรงกัน: ${codes.join(", ")}`);
      return { warn: true, message: parts.join(" | ") };
    },
    async ensurePermissionContext() {
      try {
        await this.$store.dispatch("moduleUser/ensurePermissionContextFor", {
          clinicURL: this.clinicUrl,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadPermissionContext failed", err);
      }
    },
    async refresh() {
      this.isLoading = true;
      try {
        const res = await axios.get(`/api/userMgmt/${this.clinicUrl}/users`);
        const payload = res.data || {};
        const users = Array.isArray(payload.users) ? payload.users : [];
        // Doctor = holds the DOCTOR branch role in >= 1 branch.
        this.doctors = users.filter(u => this.doctorBranches(u).length > 0);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load clinic doctors", err);
        this.doctors = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.users-card {
  border-radius: 10px;
  overflow: hidden;
  border-color: #e6eaf0;
}

.filter-bar {
  background: #fbfcfd;
}

.search-group {
  max-width: 280px;
}

/* ---------- Index ---------- */
.row-index {
  display: inline-block;
  min-width: 28px;
  color: #8a94a6;
  font-variant-numeric: tabular-nums;
  font-size: 0.82rem;
  font-weight: 500;
}

/* ---------- Name ---------- */
.user-name {
  color: #1f2937;
  font-weight: 600;
}

/* ---------- Doctor code ---------- */
.doctor-code {
  font-variant-numeric: tabular-nums;
  color: #1f2937;
  font-weight: 500;
}

/* ---------- Branch pill ---------- */
.branch-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: #f1f3f5;
  color: #495057;
  font-size: 0.78rem;
  font-weight: 500;
  border: 1px solid transparent;
}
.branch-pill--missing {
  background: #fff5f5;
  color: #c0392b;
  border-color: #f5c6c6;
}

/* ---------- Table ---------- */
.user-table >>> .user-table__head th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-top: none;
  border-bottom: 1px solid #e6eaf0;
}

.user-table >>> tbody td {
  border-top: 1px solid #f1f3f5;
  padding-top: 0.65rem;
  padding-bottom: 0.65rem;
  font-size: 0.88rem;
}

.user-table >>> tbody tr {
  transition: background-color 0.12s ease;
}
.user-table >>> tbody tr:hover {
  background-color: #fafbfc;
}
</style>
